import { ReactElement, useEffect, useState } from 'react';
import KjedeApi from 'services/KjederegisteretAdminApi/KjedeApi';
import { KjedeType } from 'services/KjederegisteretAdminApi/KjedeApi/types';

/**
 * KjedePath
 * Fleksibel komponent som:
 * 1. Kan hente kjedehierarki fra API når `kjedeNr` + `kjedeType` er gitt (som EnhetsgrupperingPath-mønsteret)
 * 2. Eller bruker allerede tilgjengelig hierarkidata (nameHierarchy/kjedeNrHierarchy)
 * 3. Eller fallback til enkelt navn + nummer
 */
interface KjedePathProps {
  /** Kjede nummer (kan være string eller number). Trigges fetch hvis kjedeType også er satt og ingen nameHierarchy er gitt */
  kjedeNr?: number | string;
  /** Kjedetype (bruk verdier fra KjedeType). Trigges fetch hvis kjedeNr også er satt og ingen nameHierarchy er gitt */
  kjedeType?: string; // KjedeType.Region | KjedeType.Marked
  /** Ferdig lastet hierarki-navn (override fetch) */
  nameHierarchy?: string[];
  /** Ferdig lastet hierarki-nummer (brukes hvis man ønsker å vise nummer pr nivå) */
  kjedeNrHierarchy?: (number | string)[];
  /** Enkelt kjedenavn (fallback) */
  kjedeNavn?: string;
  /** Vis alle nivåer med nummer (Navn (nr) / ...) i stedet for kun "Navn1 / Navn2 (sisteNr)" */
  showFullHierarchy?: boolean;
  /** Hvis true reverseres rekkefølgen (default: ikke reverse). EnhetsgrupperingPath reverserer – derfor tilbyr vi samme opsjon */
  reverse?: boolean;
}

export const KjedePath = (props: KjedePathProps): ReactElement => {
  const [fetchedNames, setFetchedNames] = useState<string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch hierarki bare hvis vi ikke allerede har nameHierarchy via props
  useEffect(() => {
    const shouldFetch = !props.nameHierarchy && props.kjedeNr && props.kjedeType;
    if (!shouldFetch) return;

    let isCancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        const nr = typeof props.kjedeNr === 'string' ? parseInt(props.kjedeNr, 10) : props.kjedeNr;
        if (!nr || isNaN(nr)) return;
        const response = await KjedeApi.getParentKjedePath(
          nr.toString(),
          props.kjedeType as KjedeType,
        );
        if (!isCancelled) {
          // response.nameHierarchy forventes i stigende rekkefølge (topp -> leaf)
          setFetchedNames(response?.nameHierarchy || []);
        }
      } catch (err) {
        if (!isCancelled) {
          // Stillegående feil – vi viser bare tom streng
          console.warn('Kunne ikke hente kjedehierarki', err);
          setFetchedNames([]);
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      isCancelled = true;
    };
  }, [props.nameHierarchy, props.kjedeNr, props.kjedeType]);

  const effectiveNames: string[] = (() => {
    if (props.nameHierarchy && props.nameHierarchy.length > 0) return props.nameHierarchy;
    if (fetchedNames && fetchedNames.length > 0) return fetchedNames;
    return [];
  })();

  const formatPath = (): string => {
    if (isLoading) return '...';

    // Bruk hierarki hvis vi har det
    if (effectiveNames.length > 0) {
      const names = props.reverse ? [...effectiveNames].reverse() : effectiveNames;
      // Full hierarki med nummer pr nivå
      if (
        props.showFullHierarchy &&
        props.kjedeNrHierarchy &&
        props.kjedeNrHierarchy.length === effectiveNames.length
      ) {
        const nums = props.reverse ? [...props.kjedeNrHierarchy].reverse() : props.kjedeNrHierarchy;
        return names.map((n, i) => `${n} (${nums[i]})`).join(' / ');
      }
      // Standard: navn / navn (sisteNr)
      const base = names.join(' / ');
      const lastNr =
        props.kjedeNrHierarchy && props.kjedeNrHierarchy[props.kjedeNrHierarchy.length - 1];
      const nr = lastNr || props.kjedeNr; // fallback
      return nr ? `${base} (${nr})` : base;
    }

    // Fallback til enkelt kjede
    if (props.kjedeNavn) {
      const nr =
        props.kjedeNr ||
        (props.kjedeNrHierarchy && props.kjedeNrHierarchy[props.kjedeNrHierarchy.length - 1]);
      return nr ? `${props.kjedeNavn} (${nr})` : props.kjedeNavn;
    }
    return props.kjedeNr ? `(${props.kjedeNr})` : '';
  };

  return <span>{formatPath()}</span>;
};
